// ── Timezone utilities ───────────────────────────────────────────────────────
// All display times in Erevis should use the user's chosen timezone.
// Default: America/Chicago (Central Time).

const DEFAULT_TZ = 'America/Chicago'

// Reuse immutable formatters, never date offsets or projected chart values.
// Daily AVWAP projections also use this path to recover each session open.
let easternHourFormatter
const chartDisplayFormatters = new Map()
const MAX_CHART_DISPLAY_FORMATTERS = 16

function chartDisplayFormatter(timezone) {
  let formatter = chartDisplayFormatters.get(timezone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    })
    if (chartDisplayFormatters.size >= MAX_CHART_DISPLAY_FORMATTERS) {
      chartDisplayFormatters.delete(chartDisplayFormatters.keys().next().value)
    }
    chartDisplayFormatters.set(timezone, formatter)
  }
  return formatter
}

/**
 * Get timezone from settings object. Call with useSettings().settings.
 */
export function getUserTimezone(settings) {
  return settings?.timezone || DEFAULT_TZ
}

/**
 * Format a date/timestamp for display in the user's timezone.
 */
export function formatTime(date, timezone, options = {}) {
  if (!date) return '\u2014'
  const tz = timezone || DEFAULT_TZ
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  if (isNaN(d)) return '\u2014'
  return d.toLocaleString('en-US', {
    timeZone: tz,
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    hour12: true,
    ...options,
  })
}

/**
 * Convert an IBKR time string to a "fake UTC" timestamp that makes
 * Lightweight Charts display the correct time in the user's timezone.
 *
 * IBKR intraday format: "20260402 09:30:00 US/Eastern"
 * IBKR daily format: "20260402"
 *
 * For daily bars: returns UTC midnight (no timezone shift needed).
 * For intraday bars: converts Eastern → user's timezone, then creates
 * a "fake UTC" epoch so Lightweight Charts renders the local time.
 */
/**
 * Convert a real UTC epoch (seconds) to a "fake UTC" timestamp for Lightweight Charts.
 * Same trick as toChartTimestamp but accepts a unix timestamp instead of an IBKR string.
 * Use this for execution timestamps, entry/exit dates on intraday charts.
 */
export function utcToChartTimestamp(utcEpochSeconds, timezone) {
  const tz = timezone || DEFAULT_TZ
  const realDate = new Date(utcEpochSeconds * 1000)
  const parts = chartDisplayFormatter(tz).formatToParts(realDate)
  const get = type => parts.find(p => p.type === type)?.value
  return Math.floor(Date.UTC(
    +get('year'), +get('month') - 1, +get('day'),
    +get('hour'), +get('minute'), +get('second')
  ) / 1000)
}

/**
 * Floor a moment to its bar-period start anchored to ET wall clock, then
 * convert through toChartTimestamp so the result is in the same fake-UTC
 * convention used by the history aggregation path.
 *
 * Canonical single source of truth for live-tick bar flooring. Previously
 * duplicated in three places; the duplicates floored 65-min bars using
 * setHours() in the user's LOCAL timezone, drifting by 1 h for any user
 * whose display tz != America/New_York.
 *
 * Signature: floorToBarPeriod(dateOrEpoch, timeframe, displayTz) -> epoch seconds
 *   - dateOrEpoch: Date | number-ms | number-seconds (seconds disambiguated
 *     by magnitude < 1e12) | invalid -> returns 0
 *   - timeframe: '1min' | '2min' | '5min' | '15min' | '30min' | '65min' | 'daily' | 'weekly'
 *     Unknown values (including undefined) return 0.
 *   - displayTz: IANA tz string; defaults to DEFAULT_TZ when undefined.
 */
function extractEtWallClock(d) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d)
  const get = (type) => +parts.find((p) => p.type === type).value
  // Intl uses hour '24' for midnight in some en-US locales; normalize.
  const hourRaw = get('hour')
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: hourRaw === 24 ? 0 : hourRaw,
    minute: get('minute'),
  }
}

function normalizeToDate(input) {
  if (input == null) return null
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input
  }
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null
    const ms = Math.abs(input) < 1e12 ? input * 1000 : input
    const d = new Date(ms)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export function floorToBarPeriod(dateOrEpoch, timeframe, displayTz) {
  const d = normalizeToDate(dateOrEpoch)
  if (!d) return 0
  const tz = displayTz || DEFAULT_TZ

  if (timeframe === 'daily') {
    return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 1000)
  }
  if (timeframe === 'weekly') return 0

  const intradayMins = { '1min': 1, '2min': 2, '5min': 5, '15min': 15, '30min': 30, '65min': 65 }
  const mins = intradayMins[timeframe]
  if (!mins) return 0

  const et = extractEtWallClock(d)
  const totalMin = et.hour * 60 + et.minute

  let barStartTotalMin
  if (mins === 65) {
    const marketOpen = 9 * 60 + 30
    const minSinceOpen = totalMin - marketOpen
    if (minSinceOpen < 0) return 0
    const barIndex = Math.floor(minSinceOpen / 65)
    barStartTotalMin = marketOpen + barIndex * 65
  } else {
    barStartTotalMin = Math.floor(totalMin / mins) * mins
  }

  const barH = String(Math.floor(barStartTotalMin / 60)).padStart(2, '0')
  const barM = String(barStartTotalMin % 60).padStart(2, '0')
  const ymd = `${et.year}${String(et.month).padStart(2, '0')}${String(et.day).padStart(2, '0')}`
  return toChartTimestamp(`${ymd} ${barH}:${barM}:00 US/Eastern`, tz)
}

export function toChartTimestamp(ibkrTimeStr, timezone) {
  const ts = String(ibkrTimeStr)
  // Daily: "20260402" → UTC midnight (timezone-independent)
  if (!ts.includes(' ')) {
    return Math.floor(Date.UTC(
      +ts.slice(0, 4), +ts.slice(4, 6) - 1, +ts.slice(6, 8)
    ) / 1000)
  }

  // Intraday: "20260402 09:30:00 US/Eastern" → target timezone display
  const m = ts.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
  if (!m) return 0
  const [, y, mo, d, h, mi, s] = m

  // Step 1: Build the real UTC time from Eastern time
  const ref = new Date(Date.UTC(+y, +mo - 1, +d, 16, 0, 0))
  easternHourFormatter ||= new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', hour12: false
  })
  const etHour = +easternHourFormatter.formatToParts(ref).find(p => p.type === 'hour').value
  const etOffset = 16 - etHour // hours from UTC (4 for EDT, 5 for EST)
  const realUTC = Date.UTC(+y, +mo - 1, +d, +h + etOffset, +mi, +s)

  // Step 2: Find the display time in the user's timezone
  const tz = timezone || DEFAULT_TZ
  const realDate = new Date(realUTC)
  const parts = chartDisplayFormatter(tz).formatToParts(realDate)
  const get = type => parts.find(p => p.type === type)?.value

  // Step 3: Create a "fake UTC" timestamp with the local time values
  // This tricks Lightweight Charts into displaying the user's local time
  return Math.floor(Date.UTC(
    +get('year'), +get('month') - 1, +get('day'),
    +get('hour'), +get('minute'), +get('second')
  ) / 1000)
}
